import { useState } from "react"
import { Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { faBurgerGlass } from "@fortawesome/pro-solid-svg-icons"
import { ErrorBoundary } from "react-error-boundary"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { tailwindPalette } from "../../../../../../theme/tailwindPalette"
import { EmptyContent, EmptyContentVariant } from "../../../../../../../shared/components/skeleton/EmptyContent"
import { DatePeriod } from "../../../../../../../shared/components/filters/selectDatePeriodFilter/SelectDatePeriodFilter"
import { PurchaseOrder } from "../../../weekMenuTypes"
import { UserPurchases } from "../../../../upcomingOrders/purchases/pages/UserPurchases"
import { PurchaseOrderTypeFilter } from "../../../../upcomingOrders/purchases/purchasesApi"

import { UserDashboardErrorFallbackCard } from "../../UserDashboardErrorFallbackCard"
import { useListPagination } from "../../../../../../../shared/hooks/useListPagination"
import { MeetingListPagination } from "../../../../../../serviceCenter/report/home/components/meetigs/MeetingListPagination"
import { UpcomingOrdersPanel } from "../UpcomingOrdersPanel"
import { UpcomingOrdersModal } from "../UpcomingOrdersModal"
import { PurchaseRow } from "../purchases/PurchaseRow"

const useStyles = makeStyles()((theme) => ({
    rowsContainer: {
        display: "flex",
        flexDirection: "column",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    errorCard: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
    errorContent: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
    },
}))

type TakeawayPanelProps = Readonly<{
    takeaways: ReadonlyArray<PurchaseOrder>
    period: DatePeriod
}>

export function TakeawayPanel({ takeaways, period }: TakeawayPanelProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const { pagedItems, pagination } = useListPagination(takeaways)

    return (
        <>
            <UpcomingOrdersPanel
                icon={faBurgerGlass}
                iconBackgroundColor={tailwindPalette.amber[200]}
                iconColor={tailwindPalette.amber[900]}
                titleId="user-home-upcoming-takeaway"
                titleFallback="Takeaway"
                counter={pagination.totalItems}
                onShowAll={() => setIsModalOpen(true)}
                collapseAriaLabel={l10n.getString("user-home-takeaway-collapse")}
                expandAriaLabel={l10n.getString("user-home-takeaway-expand")}
                footer={pagination.totalItems > 0 ? <MeetingListPagination {...pagination} /> : undefined}
            >
                {!pagination.totalItems ? (
                    <LocalizedStrict id="user-home-upcoming-takeaway-no-orders" attrs={{ title: true }}>
                        <EmptyContent variant={EmptyContentVariant.SINGLE_LEFT} title="No takeaway in period" />
                    </LocalizedStrict>
                ) : (
                    <Box className={classes.rowsContainer}>
                        {pagedItems.map((takeaway) => (
                            <ErrorBoundary
                                key={takeaway.id}
                                fallback={
                                    <UserDashboardErrorFallbackCard
                                        classNames={classes.errorCard}
                                        contentClassNames={classes.errorContent}
                                    />
                                }
                            >
                                <PurchaseRow purchase={takeaway} purchases={takeaways} />
                            </ErrorBoundary>
                        ))}
                    </Box>
                )}
            </UpcomingOrdersPanel>
            {isModalOpen && (
                <LocalizedStrict id="user-home-upcoming-takeaway-modal-title" attrs={{ title: true }}>
                    <UpcomingOrdersModal open onClose={() => setIsModalOpen(false)} title="Takeaway">
                        <UserPurchases orderType={PurchaseOrderTypeFilter.TAKEAWAY} initialPeriod={period} />
                    </UpcomingOrdersModal>
                </LocalizedStrict>
            )}
        </>
    )
}
